import React from 'react';
import {
  Box,
  Drawer,
  List,
  Divider, 
  Avatar, 
  Typography, 
  ListItemButton, 
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  IconButton,
  AppBar,
  Toolbar,
  useMediaQuery,
  Badge,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccountBalanceWallet as WalletIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { filterMenuItems } from '../utils/permissions';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 280;

const Sidebar = ({ menuItems, onNavigate, mobileOpen, handleDrawerToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isDarkMode, toggleTheme, mode } = useCustomTheme();
  const { showToast } = useToast();

  // Fetch unread notifications count
  const { data: unreadCount } = useQuery({
    queryKey: ['unreadNotifications'],
    queryFn: () => api.notifications.getUnreadCount(),
    refetchInterval: 10000, // Check every 10 seconds
  });

  const handleLogout = () => {
    try {
      // Clear all auth-related data from localStorage
      localStorage.clear();
      // Clear sessionStorage if any
      sessionStorage.clear();
      // Show success toast
      showToast('Logged out successfully', 'success');
      // Force reload the page to clear any cached data
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000); // Wait for toast to be visible
    } catch (error) {
      console.error('Logout error:', error);
      showToast('Error during logout', 'error');
      // Fallback to direct navigation if something goes wrong
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    }
  };

  // Get user data - prioritize AuthContext, fallback to localStorage
  const getUserName = () => {
    if (user && user.fullname) {
      return user.fullname;
    }
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        return userData.fullname || 'Administrator';
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return 'Administrator';
  };

  const getUserRole = () => {
    if (user && user.type) {
      return user.type;
    }
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        return userData.type || 'admin';
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return 'admin';
  };

  const getUserPermissions = () => {
    // First try to get from AuthContext (most up-to-date)
    if (user && user.permissions) {
      return Array.isArray(user.permissions) ? user.permissions : [];
    }
    
    // Fallback to localStorage
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        return Array.isArray(userData.permissions) ? userData.permissions : [];
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return [];
  };

  const getFilteredMenuItems = () => {
    // Use AuthContext user if available, otherwise fallback to localStorage
    const userRole = getUserRole();
    const userPermissions = getUserPermissions();
    
    // Debug logging (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('Sidebar - User Role:', userRole);
      console.log('Sidebar - User Permissions:', userPermissions);
      console.log('Sidebar - Has view_affiliates:', userPermissions.includes('view_affiliates'));
      console.log('Sidebar - Menu Items:', menuItems.map(item => item.text));
    }
    
    // Use permission-based filtering
    return filterMenuItems(menuItems, userPermissions, userRole);
  };

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      bgcolor: theme.palette.background.paper,
      background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
    }}>
      <Box 
        sx={{ 
          p: 3, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
        }}
      >
        <Avatar 
          sx={{ 
            bgcolor: theme.palette.primary.main,
            width: 48,
            height: 48,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
            fontSize: '1.5rem',
            fontWeight: 600,
          }}
        >
          {getUserName().charAt(0)}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              letterSpacing: '0.5px',
              mb: 0.5,
            }}
          >
            {getUserName().split(' ')[0]+'...'}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.palette.text.secondary,
              display: 'block',
            }}
          >
            {getUserRole() === 'superadmin' ? 'Super Admin' : 'Admin'} Dashboard
          </Typography>
        </Box>
        <IconButton onClick={toggleTheme} color="inherit">
          {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ px: 2, py: 3 }}>
        {getFilteredMenuItems().map((item) => {
          const isActive = currentPath === item.path;
          
          // Debug logging for Affiliate Management
          if (item.text === 'Affiliate Management' && process.env.NODE_ENV === 'development') {
            console.log('Rendering Affiliate Management menu item');
            console.log('Filtered menu items count:', getFilteredMenuItems().length);
            console.log('All menu items:', menuItems.map(i => i.text));
          }
          return (
            <ListItemButton
              key={item.text}
              onClick={() => {
                onNavigate(item.path);
                if (isMobile) {
                  handleDrawerToggle();
                }
              }}
              sx={{
                borderRadius: 2,
                mb: 1,
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '4px',
                  height: isActive ? '70%' : '0%',
                  bgcolor: theme.palette.primary.main,
                  borderRadius: '0 4px 4px 0',
                  transition: 'all 0.3s ease',
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  '&::before': {
                    height: '70%',
                  },
                  '& .MuiListItemIcon-root': {
                    transform: 'scale(1.1)',
                    color: theme.palette.primary.main,
                  },
                  '& .MuiListItemText-primary': {
                    color: theme.palette.primary.main,
                  },
                },
                ...(isActive && {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.primary.main,
                    transform: 'scale(1.1)',
                  },
                  '& .MuiListItemText-primary': {
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  },
                }),
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                  transition: 'all 0.3s ease',
                  minWidth: 40,
                  fontSize: '1.25rem',
                }}
              >
                {item.text === 'All Notifications' ? (
                  <Badge 
                    badgeContent={unreadCount?.count || 0} 
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        animation: unreadCount?.count > 0 ? 'pulse 2s infinite' : 'none',
                      },
                      '@keyframes pulse': {
                        '0%': {
                          transform: 'scale(1)',
                          opacity: 1,
                        },
                        '50%': {
                          transform: 'scale(1.2)',
                          opacity: 0.8,
                        },
                        '100%': {
                          transform: 'scale(1)',
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{
                  sx: {
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                    transition: 'all 0.3s ease',
                    fontSize: '0.95rem',
                  }
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <List sx={{ px: 2, py: 1 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            mb: 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: alpha(theme.palette.error.main, 0.08),
              '& .MuiListItemIcon-root': {
                color: theme.palette.error.main,
                transform: 'scale(1.1)',
              },
              '& .MuiListItemText-primary': {
                color: theme.palette.error.main,
              },
            },
          }}
        >
          <ListItemIcon 
            sx={{ 
              color: theme.palette.error.main,
              transition: 'all 0.3s ease',
              minWidth: 40,
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout" 
            primaryTypographyProps={{
              sx: {
                fontWeight: 500,
                color: theme.palette.error.main,
                transition: 'all 0.3s ease',
                fontSize: '0.95rem',
              }
            }}
          />
        </ListItemButton>
      </List>
      <Box 
        sx={{
          p: 3, 
          textAlign: 'center',
          bgcolor: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 500,
            display: 'block',
            mb: 0.5,
          }}
        >
          Version 1.0.0
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          &copy; {new Date().getFullYear()} Utilita
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: theme.palette.background.paper,
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: { xs: 'block', sm: 'none' },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, color: theme.palette.text.primary }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ color: theme.palette.text.primary }}>
            Dashboard
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              bgcolor: theme.palette.background.paper,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[1],
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
};

export default Sidebar; 